import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const APP_OPTIONS = [
  { id: 'gmail', label: 'Gmail', cost: 0, icon: '📧', isAI: false },
  { id: 'outlook', label: 'Outlook / Microsoft 365', cost: 99, icon: '📨', isAI: false },
  { id: 'notion', label: 'Notion', cost: 96, icon: '📝', isAI: false },
  { id: 'google_calendar', label: 'Google Calendar', cost: 0, icon: '📅', isAI: false },
  { id: 'whatsapp', label: 'WhatsApp', cost: 0, icon: '💬', isAI: false },
  { id: 'todoist', label: 'Todoist', cost: 48, icon: '✅', isAI: false },
  { id: 'calendly', label: 'Calendly', cost: 96, icon: '🗓️', isAI: false },
  { id: 'tripit', label: 'TripIt Pro', cost: 49, icon: '✈️', isAI: false },
  { id: 'fantastical', label: 'Fantastical', cost: 58, icon: '📆', isAI: false },
  { id: 'chatgpt', label: 'ChatGPT Plus', cost: 240, icon: '🤖', isAI: true },
  { id: 'claude', label: 'Claude (Anthropic)', cost: 240, icon: '🌀', isAI: true },
  { id: 'gemini', label: 'Google Gemini', cost: 240, icon: '✨', isAI: true },
];

const questions = [
  { id: 'scheduling', emoji: '📅', question: 'How often do you miss or scramble to reschedule appointments?', type: 'single', options: [{ label: "All the time — it's chaos", value: 3 }, { label: "More than I'd like", value: 2 }, { label: 'Occasionally', value: 1 }, { label: 'I have it under control', value: 0 }] },
  { id: 'travel', emoji: '✈️', question: 'How much time do you spend booking travel or planning trips?', type: 'single', options: [{ label: "Hours — it's exhausting", value: 3 }, { label: 'A fair amount each month', value: 2 }, { label: 'Only for big trips', value: 1 }, { label: 'Rarely travel or use an agent', value: 0 }] },
  { id: 'inbox', emoji: '📬', question: 'How does your inbox make you feel on a Monday morning?', type: 'single', options: [{ label: "Pure dread — it's overwhelming", value: 3 }, { label: "A bit stressed, honestly", value: 2 }, { label: 'Manageable but annoying', value: 1 }, { label: "Fine — I keep on top of it", value: 0 }] },
  { id: 'lifeadmin', emoji: '🧾', question: 'How much life admin piles up that you keep putting off?', type: 'single', options: [{ label: 'So much — where do I start?', value: 3 }, { label: 'Quite a bit each week', value: 2 }, { label: 'A few things here and there', value: 1 }, { label: 'I stay pretty on top of it', value: 0 }] },
  { id: 'reminders', emoji: '🎂', question: 'Do you ever forget important dates (birthdays, bills, calls)?', type: 'single', options: [{ label: "Yes — and I feel terrible about it", value: 3 }, { label: "More than I'd like to admit", value: 2 }, { label: 'Sometimes slip through', value: 1 }, { label: "No, I'm quite organised", value: 0 }] },
  { id: 'ai_familiarity', emoji: '🤖', question: 'How familiar are you with AI tools like ChatGPT or Claude?', type: 'single', options: [{ label: "I use them daily — they're part of my workflow", value: 3 }, { label: "I've tried them and like them", value: 2 }, { label: "I've heard of them but haven't really used them", value: 1 }, { label: "Not really my thing — I'd want help getting started", value: 0 }] },
  { id: 'apps', emoji: '📱', question: 'Which of these apps do you currently use or pay for?', type: 'multi', options: APP_OPTIONS },
];

const VALID_PLAN_KEYS = ['personal', 'personal-plus', 'personal-support', 'personal-plus-support'];

const scrollToPricing = () => {
  const el = document.getElementById('pricing');
  if (!el) return;
  window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' });
};

const getResult = (score, answers, selectedApps) => {
  const highAreas = [];
  if (answers.scheduling >= 2) highAreas.push({ icon: '📅', label: 'Calendar & scheduling', desc: 'Claw will manage your calendar, move appointments, and prevent clashes — without you lifting a finger.' });
  if (answers.travel >= 2) highAreas.push({ icon: '✈️', label: 'Travel planning', desc: 'Claw researches flights, hotels, and builds full itineraries. You just show up.' });
  if (answers.inbox >= 2) highAreas.push({ icon: '📬', label: 'Inbox & messages', desc: 'Claw summarises threads, flags what matters, and drafts replies in your tone.' });
  if (answers.lifeadmin >= 2) highAreas.push({ icon: '🧾', label: 'Life admin', desc: "From car bookings to grocery lists — Claw handles the boring stuff so you don't have to." });
  if (answers.reminders >= 2) highAreas.push({ icon: '🎂', label: 'Reminders & dates', desc: 'Claw tracks birthdays, bills, and calls — and nudges you at exactly the right moment.' });

  const aiScore = answers.ai_familiarity || 0;
  const needsSupport = score >= 8 || aiScore <= 1;
  const needsPlus = answers.travel >= 2 || score >= 10;

  const selectedAppObjs = selectedApps.map(id => APP_OPTIONS.find(a => a.id === id)).filter(Boolean);
  const aiApps = selectedAppObjs.filter(a => a.isAI);
  const productivityApps = selectedAppObjs.filter(a => !a.isAI);
  const totalSavings = productivityApps.reduce((sum, app) => sum + (app.cost || 0), 0);

  let recommendedPlan;
  if (needsPlus && needsSupport) {
    recommendedPlan = { key: 'personal-plus-support', name: 'Personal Plus + Support', price: '£899 + £99/mo', reason: "You've got a lot going on — travel, admin, scheduling and more. Personal Plus gives you the full toolkit, and ongoing support means Claw keeps getting sharper as your life evolves.", badge: 'Best for your lifestyle', badgeColor: 'bg-[#1A1A18] text-white' };
  } else if (needsPlus) {
    recommendedPlan = { key: 'personal-plus', name: 'Personal Plus', price: '£899 one-time', reason: "Your needs go beyond the basics — travel planning, advanced scheduling, and a fully tuned assistant. Personal Plus is built exactly for you.", badge: 'Recommended for you', badgeColor: 'bg-[#2D6A8F] text-white' };
  } else if (needsSupport) {
    recommendedPlan = { key: 'personal-support', name: 'Personal + Support', price: '£499 + £99/mo', reason: "Personal covers everything you need, and with ongoing support your Claw will keep improving — no extra effort from you.", badge: 'Great fit for you', badgeColor: 'bg-[#3D7A5E] text-white' };
  } else {
    recommendedPlan = { key: 'personal', name: 'Personal', price: '£499 one-time', reason: "You're organised and tech-savvy — Personal is the perfect starting point. Everything you need, nothing you don't.", badge: 'Perfect starting point', badgeColor: 'bg-[#5C4A2A] text-white' };
  }

  const tone = score >= 10 ? 'high' : score >= 5 ? 'mid' : 'low';
  const headlines = { high: 'Claw could genuinely transform your week.', mid: 'Claw would save you several hours a week.', low: 'Claw could be a quiet superpower for you.' };
  const sublines = { high: "You're juggling a lot. Here's where Claw would make the biggest difference:", mid: "You're managing well, but these areas could be a lot smoother:", low: "You're already organised — Claw would just make it effortless:" };

  return { headline: headlines[tone], subline: sublines[tone], tone, areas: highAreas.length ? highAreas : [{ icon: '✨', label: 'Peace of mind', desc: 'Even organised people love having a brilliant assistant in their corner.' }], recommendedPlan, aiScore, totalSavings, productivityApps, aiApps };
};

const commitRecommendation = (planKey) => {
  if (!planKey || !VALID_PLAN_KEYS.includes(planKey)) return;
  sessionStorage.setItem('clawRecommendedPlan', planKey);
  window.dispatchEvent(new Event('clawPlanUpdated'));
};

const ProgressBar = ({ current, total }) => (
  <div className="flex items-center gap-3 mb-8">
    <div className="flex gap-1.5 flex-1">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-400 ${i < current ? 'bg-[#2D6A8F]' : 'bg-[#E8E2DA]'}`} />
      ))}
    </div>
    <span className="text-[11px] font-mono-custom text-[#9E9690] flex-shrink-0">{current}/{total}</span>
  </div>
);

const ResultCard = ({ area, index }) => (
  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + index * 0.1 }}
    className="flex items-start gap-3 p-4 bg-white border border-[#E8E2DA] rounded-xl">
    <span className="text-xl flex-shrink-0 mt-0.5">{area.icon}</span>
    <div>
      <p className="text-[13px] font-bold text-[#1A1A18] mb-0.5">{area.label}</p>
      <p className="text-[12px] text-[#6B6560] leading-relaxed">{area.desc}</p>
    </div>
  </motion.div>
);

const AppSavingsCard = ({ productivityApps, aiApps, totalSavings }) => {
  const hasProductivity = productivityApps.length > 0;
  const hasAI = aiApps.length > 0;
  if (!hasProductivity && !hasAI) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="mt-4 space-y-3">
      {hasProductivity && (
        <div className="p-4 bg-[#EBF5F0] border border-[#8ACAAA] rounded-xl">
          <div className="flex items-center gap-2 mb-2"><span className="text-base">💰</span><p className="text-[12px] font-bold text-[#2A6B4A] uppercase tracking-wider font-mono-custom">Apps Claw can replace</p></div>
          <div className="flex flex-wrap gap-1.5 mb-2">{productivityApps.map(app => (<span key={app.id} className="text-[11px] px-2 py-0.5 bg-white border border-[#8ACAAA] rounded-full text-[#2A6B4A] font-medium">{app.icon} {app.label}</span>))}</div>
          {totalSavings > 0 ? <p className="text-[12px] text-[#3D7A5E] leading-relaxed">Claw handles what these apps do for you — potentially saving you <strong>up to £{totalSavings}/yr</strong> in subscriptions.</p> : <p className="text-[12px] text-[#3D7A5E] leading-relaxed">Claw integrates directly with these tools — no switching apps, no disruption.</p>}
        </div>
      )}
      {hasAI && (
        <div className="p-4 bg-[#EBF2F9] border border-[#C8DDE9] rounded-xl">
          <div className="flex items-center gap-2 mb-2"><span className="text-base">🔌</span><p className="text-[12px] font-bold text-[#2D6A8F] uppercase tracking-wider font-mono-custom">Your AI — now connected</p></div>
          <div className="flex flex-wrap gap-1.5 mb-2">{aiApps.map(app => (<span key={app.id} className="text-[11px] px-2 py-0.5 bg-white border border-[#C8DDE9] rounded-full text-[#2D6A8F] font-medium">{app.icon} {app.label}</span>))}</div>
          <p className="text-[12px] text-[#2D6A8F] leading-relaxed">Claw <strong>runs on</strong> AI models like these — so your existing subscription does even more. We'll connect it during setup so nothing goes to waste.</p>
        </div>
      )}
    </motion.div>
  );
};

const AIFamiliarityNote = ({ aiScore }) => {
  if (aiScore >= 2) return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
      className="mt-3 flex items-start gap-2.5 px-4 py-3 bg-[#EBF2F9] border border-[#C8DDE9] rounded-xl">
      <span className="text-base flex-shrink-0">🧠</span>
      <p className="text-[12px] text-[#2D6A8F] leading-relaxed"><strong>You're already AI-savvy.</strong> Claw will feel immediately familiar — think of it as your AI tools finally working together with a single point of contact.</p>
    </motion.div>
  );
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
      className="mt-3 flex items-start gap-2.5 px-4 py-3 bg-[#FBF4E8] border border-[#DFC98A] rounded-xl">
      <span className="text-base flex-shrink-0">👋</span>
      <p className="text-[12px] text-[#8A6A2A] leading-relaxed"><strong>New to AI? No problem.</strong> Claw is designed to feel like messaging a person — not configuring software. Our team sets everything up and guides you through it.</p>
    </motion.div>
  );
};

const PlanRecommendation = ({ plan }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
    className="mt-5 rounded-2xl border border-[#C8DDE9] bg-[#EBF2F9]/50 overflow-hidden">
    <div className="px-5 py-4 border-b border-[#C8DDE9] flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2.5">
        <SafeIcon icon={FiIcons.FiStar} className="text-[#2D6A8F] text-base flex-shrink-0" />
        <p className="text-[12px] font-bold text-[#2D6A8F] uppercase tracking-wider font-mono-custom">Our recommendation for you</p>
      </div>
      <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${plan.badgeColor}`}>{plan.badge}</span>
    </div>
    <div className="px-5 py-4">
      <h4 className="text-[17px] font-extrabold text-[#1A1A18] tracking-tight">{plan.name}</h4>
      <p className="text-[13px] text-[#9E9690] font-mono-custom mt-0.5 mb-3">{plan.price}</p>
      <p className="text-[13px] text-[#6B6560] leading-relaxed mb-4">{plan.reason}</p>
      <button onClick={scrollToPricing}
        className="w-full py-3 rounded-xl bg-[#1A1A18] text-[#F8F6F3] text-[13px] font-semibold flex items-center justify-center gap-2 hover:bg-[#2A2A26] transition-colors">
        See full pricing details
        <SafeIcon icon={FiIcons.FiArrowDown} className="text-xs" />
      </button>
    </div>
  </motion.div>
);

const MultiSelectQuestion = ({ question, selected, onToggle, onContinue }) => {
  const productivityOpts = question.options.filter(o => !o.isAI);
  const aiOpts = question.options.filter(o => o.isAI);
  return (
    <div>
      <div className="mb-6">
        <span className="text-4xl mb-4 block">{question.emoji}</span>
        <h3 className="text-[19px] font-bold text-[#1A1A18] leading-snug mb-1">{question.question}</h3>
        <p className="text-[12px] text-[#9E9690]">Select all that apply.</p>
      </div>
      <div className="mb-4">
        <p className="text-[10px] font-bold text-[#9E9690] uppercase tracking-widest font-mono-custom mb-2">Productivity apps</p>
        <div className="grid grid-cols-2 gap-2">
          {productivityOpts.map((opt) => {
            const isSelected = selected.includes(opt.id);
            return (
              <button key={opt.id} onClick={() => onToggle(opt.id)}
                className={`text-left px-3 py-2.5 rounded-xl border text-[12px] font-medium transition-all duration-150 flex items-center gap-2 ${isSelected ? 'bg-[#1A1A18] text-[#F8F6F3] border-[#1A1A18]' : 'bg-white border-[#E8E2DA] text-[#1A1A18] hover:border-[#B8CDD9] hover:bg-[#EBF2F9]/30'}`}>
                <span>{opt.icon}</span><span className="leading-tight">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="mb-5">
        <p className="text-[10px] font-bold text-[#9E9690] uppercase tracking-widest font-mono-custom mb-1">AI subscriptions</p>
        <p className="text-[10px] text-[#9E9690] mb-2">Claw runs on these — we'll connect yours during setup</p>
        <div className="grid grid-cols-2 gap-2">
          {aiOpts.map((opt) => {
            const isSelected = selected.includes(opt.id);
            return (
              <button key={opt.id} onClick={() => onToggle(opt.id)}
                className={`text-left px-3 py-2.5 rounded-xl border text-[12px] font-medium transition-all duration-150 flex items-center gap-2 ${isSelected ? 'bg-[#2D6A8F] text-white border-[#2D6A8F]' : 'bg-white border-[#C8DDE9] text-[#1A1A18] hover:border-[#2D6A8F] hover:bg-[#EBF2F9]/30'}`}>
                <span>{opt.icon}</span><span className="leading-tight">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <button onClick={onContinue}
        className="w-full py-3.5 rounded-xl bg-[#1A1A18] text-[#F8F6F3] text-[14px] font-semibold flex items-center justify-center gap-2 hover:bg-[#2A2A26] transition-colors">
        {selected.length === 0 ? "I don't use any of these" : `Continue with ${selected.length} selected`}
        <SafeIcon icon={FiIcons.FiArrowRight} className="text-xs" />
      </button>
    </div>
  );
};

const UseCaseQuiz = () => {
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selected, setSelected] = useState(null);
  const [selectedApps, setSelectedApps] = useState([]);
  const [finished, setFinished] = useState(false);
  const [quizResult, setQuizResult] = useState(null);

  // Clear any stale recommendation when the quiz mounts
  useEffect(() => {
    sessionStorage.removeItem('clawRecommendedPlan');
    window.dispatchEvent(new Event('clawPlanUpdated'));
  }, []);

  const question = questions[currentQ];
  const isMulti = question.type === 'multi';

  const toneColors = {
    high: { badge: 'bg-[#FBF4E8] border-[#DFC98A] text-[#8A6A2A]' },
    mid: { badge: 'bg-[#EBF2F9] border-[#C8DDE9] text-[#2D6A8F]' },
    low: { badge: 'bg-[#EBF5F0] border-[#8ACAAA] text-[#2A6B4A]' },
  };

  const finaliseResult = (finalAnswers, apps) => {
    const score = Object.entries(finalAnswers)
      .filter(([k]) => k !== 'ai_familiarity' && k !== 'apps')
      .reduce((a, [, b]) => a + b, 0);
    const result = getResult(score, finalAnswers, apps);
    if (result?.recommendedPlan && VALID_PLAN_KEYS.includes(result.recommendedPlan.key)) {
      commitRecommendation(result.recommendedPlan.key);
    }
    setQuizResult(result);
    setFinished(true);
  };

  const advance = (newAnswers, apps) => {
    if (currentQ < questions.length - 1) {
      setCurrentQ(currentQ + 1);
      setSelected(null);
    } else {
      finaliseResult(newAnswers, apps ?? selectedApps);
    }
  };

  const handleSelect = (value) => {
    if (selected !== null) return;
    setSelected(value);
    const newAnswers = { ...answers, [question.id]: value };
    setAnswers(newAnswers);
    setTimeout(() => advance(newAnswers, undefined), 600);
  };

  const toggleApp = (id) => setSelectedApps(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);

  const handleAppsContinue = () => {
    const newAnswers = { ...answers, apps: selectedApps };
    setAnswers(newAnswers);
    advance(newAnswers, selectedApps);
  };

  const reset = () => {
    setCurrentQ(0);
    setAnswers({});
    setSelected(null);
    setSelectedApps([]);
    setFinished(false);
    setQuizResult(null);
    sessionStorage.removeItem('clawRecommendedPlan');
    window.dispatchEvent(new Event('clawPlanUpdated'));
  };

  return (
    <section id="quiz" className="py-32 bg-white relative">
      <div className="max-w-2xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="tag-pill mx-auto mb-5 w-fit">2-minute quiz</div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-[#1A1A18] mb-4 tracking-tight leading-tight" style={{ letterSpacing: '-0.02em' }}>
            What could Claw<br />
            <span className="gradient-text-accent">do for you?</span>
          </h2>
          <p className="text-[#6B6560] text-base leading-relaxed max-w-md mx-auto">
            Answer a few quick questions and we'll show you exactly how Claw would fit into your life — and which plan makes sense.
          </p>
        </div>

        <motion.div className="bg-[#F8F6F3] border border-[#E8E2DA] rounded-2xl p-8 shadow-sm" layout transition={{ duration: 0.3 }}>
          <AnimatePresence mode="wait">
            {!finished ? (
              <motion.div key={`q-${currentQ}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                <ProgressBar current={currentQ + 1} total={questions.length} />
                {isMulti ? (
                  <MultiSelectQuestion question={question} selected={selectedApps} onToggle={toggleApp} onContinue={handleAppsContinue} />
                ) : (
                  <div>
                    <div className="mb-8">
                      <span className="text-4xl mb-4 block">{question.emoji}</span>
                      <h3 className="text-[19px] font-bold text-[#1A1A18] leading-snug">{question.question}</h3>
                    </div>
                    <div className="space-y-3">
                      {question.options.map((opt, i) => (
                        <button key={i} onClick={() => handleSelect(opt.value)} disabled={selected !== null}
                          className={`w-full text-left px-5 py-4 rounded-xl border text-[14px] font-medium transition-all duration-200 ${selected === opt.value ? 'bg-[#1A1A18] text-[#F8F6F3] border-[#1A1A18] scale-[0.99]' : selected !== null ? 'bg-white border-[#E8E2DA] text-[#9E9690] cursor-default' : 'bg-white border-[#E8E2DA] text-[#1A1A18] hover:border-[#B8CDD9] hover:bg-[#EBF2F9]/30 cursor-pointer'}`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            ) : quizResult ? (
              <motion.div key="result" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
                <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold border mb-5 ${toneColors[quizResult.tone].badge}`}>
                  <SafeIcon icon={FiIcons.FiCheckCircle} className="text-[11px]" />
                  Your personalised result
                </div>
                <h3 className="text-[22px] font-extrabold text-[#1A1A18] mb-2 leading-snug tracking-tight" style={{ letterSpacing: '-0.02em' }}>{quizResult.headline}</h3>
                <p className="text-[14px] text-[#6B6560] mb-5 leading-relaxed">{quizResult.subline}</p>
                <div className="space-y-3 mb-2">{quizResult.areas.map((area, i) => <ResultCard key={i} area={area} index={i} />)}</div>
                <AIFamiliarityNote aiScore={quizResult.aiScore} />
                <AppSavingsCard productivityApps={quizResult.productivityApps} aiApps={quizResult.aiApps} totalSavings={quizResult.totalSavings} />
                {quizResult.recommendedPlan && VALID_PLAN_KEYS.includes(quizResult.recommendedPlan.key) && (
                  <PlanRecommendation plan={quizResult.recommendedPlan} />
                )}
                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <button onClick={reset} className="btn-secondary flex items-center justify-center gap-2 text-[14px]">
                    <SafeIcon icon={FiIcons.FiRefreshCw} className="text-xs text-[#6B6560]" />
                    Retake quiz
                  </button>
                </div>
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
                  className="font-handwritten text-[16px] text-[#C17B4A] mt-5 text-center" style={{ transform: 'rotate(-0.5deg)' }}>
                  ✎ most people save 5–8 hrs in their first week
                </motion.p>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default UseCaseQuiz;